import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { CaretLeft, Check, X, Plus, Trash, UploadSimple, File as FileIcon, MapPin, CalendarBlank, Car, CurrencyDollar, User, ShieldCheck, Package, Receipt, WarningCircle, CheckCircle, Clock, ArrowRight, FilePdf, PencilSimple, FloppyDisk, CreditCard } from "@phosphor-icons/react";

const STATUS_COLORS = {
  solicitado: "bg-blue-100 text-blue-800 border-blue-200",
  aprobado: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rechazado: "bg-red-100 text-red-800 border-red-200",
  en_viaje: "bg-amber-100 text-amber-800 border-amber-200",
  en_liquidacion: "bg-purple-100 text-purple-800 border-purple-200",
  liquidado: "bg-teal-100 text-teal-800 border-teal-200",
  cerrado: "bg-slate-200 text-slate-700 border-slate-300",
};
const STATUS_LABELS = { solicitado: "Solicitado", aprobado: "Aprobado", rechazado: "Rechazado", en_viaje: "En Viaje", en_liquidacion: "En Liquidacion", liquidado: "Liquidado", cerrado: "Cerrado" };
const VISIT_LABELS = { fidelizacion: "Fidelizacion", demostracion: "Demostracion", prospeccion: "Prospeccion" };
const CATEGORY_LABELS = { alimentos: "Alimentos", hospedaje: "Hospedaje", casetas: "Casetas", autobus: "Autobus", vuelos: "Vuelos", gasolina: "Gasolina", incidentes: "Incidentes", otros: "Otros" };
const CATEGORY_ICONS = { alimentos: "🍽", hospedaje: "🏨", casetas: "🛣", autobus: "🚌", vuelos: "✈", gasolina: "⛽", incidentes: "⚠", otros: "📦" };
const SAFETY_LABELS = { chaleco: "Chaleco", zapato_industrial: "Zapato Industrial", casco: "Casco", tapon_auditivo: "Tapon Auditivo", lentes: "Lentes" };
const PAYMENT_LABELS = { efectivo: "Efectivo", transferencia: "Transferencia", cheque: "Cheque", tarjeta_credito: "Tarjeta de Credito", reembolso: "Reembolso al Empleado" };

export function ViaticosDetail({ solicitudId, onBack }) {
  const { user, token } = useAuth();
  const [sol, setSol] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveData, setApproveData] = useState({ approved_amount: "", admin_notes: "" });
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: "otros", date: "", description: "", amount: "", receipt_type: "nota", receipt_number: "", charge_to: "sti", charge_client_name: "", payment_method: "efectivo", payment_transfer_id: "", payment_transfer_extra: "" });
  const [editingBudget, setEditingBudget] = useState(false);
  const [editBudgetItems, setEditBudgetItems] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const isOwner = sol?.user_id === user?.id;

  const load = useCallback(async () => {
    try {
      const [sRes, sumRes] = await Promise.all([
        axios.get(`${API}/viaticos/solicitudes/${solicitudId}`, { headers }),
        axios.get(`${API}/viaticos/solicitudes/${solicitudId}/summary`, { headers }),
      ]);
      setSol(sRes.data);
      setSummary(sumRes.data);
      setApproveData({ approved_amount: sRes.data.amount_requested || "", admin_notes: sRes.data.admin_notes || "" });
    } catch { toast.error("Error cargando solicitud"); }
    setLoading(false);
  }, [solicitudId, token]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    try {
      await axios.put(`${API}/viaticos/solicitudes/${solicitudId}/approve`, approveData, { headers });
      toast.success("Solicitud aprobada");
      setShowApproveModal(false);
      load();
    } catch { toast.error("Error al aprobar"); }
  };

  const handleReject = async () => {
    try {
      await axios.put(`${API}/viaticos/solicitudes/${solicitudId}/reject`, { admin_notes: approveData.admin_notes }, { headers });
      toast.success("Solicitud rechazada");
      setShowApproveModal(false);
      load();
    } catch { toast.error("Error"); }
  };

  const changeStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/viaticos/solicitudes/${solicitudId}/status`, { status: newStatus }, { headers });
      toast.success(`Estatus cambiado a ${STATUS_LABELS[newStatus]}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const addExpense = async () => {
    if (!expenseForm.date || !expenseForm.amount) { toast.error("Fecha y monto son requeridos"); return; }
    try {
      await axios.post(`${API}/viaticos/solicitudes/${solicitudId}/gastos`, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
      }, { headers });
      toast.success("Gasto registrado");
      setShowExpenseForm(false);
      setExpenseForm({ category: "otros", date: "", description: "", amount: "", receipt_type: "nota", receipt_number: "", charge_to: "sti", charge_client_name: "", payment_method: "efectivo", payment_transfer_id: "", payment_transfer_extra: "" });
      load();
    } catch { toast.error("Error al registrar gasto"); }
  };

  const deleteExpense = async (gastoId) => {
    if (!window.confirm("Eliminar este gasto?")) return;
    try {
      await axios.delete(`${API}/viaticos/gastos/${gastoId}`, { headers });
      toast.success("Gasto eliminado");
      load();
    } catch { toast.error("Error"); }
  };

  const uploadFile = async (gastoId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/viaticos/gastos/${gastoId}/files`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Archivo subido");
      load();
    } catch { toast.error("Error al subir archivo"); }
  };

  const startEditBudget = () => {
    setEditBudgetItems((sol?.budget_items || []).map(b => ({ ...b })));
    setEditingBudget(true);
  };

  const saveBudget = async () => {
    try {
      await axios.put(`${API}/viaticos/solicitudes/${solicitudId}/budget`, { budget_items: editBudgetItems }, { headers });
      toast.success("Presupuesto actualizado");
      setEditingBudget(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const exportPdf = (type) => {
    window.open(`${API}/viaticos/solicitudes/${solicitudId}/pdf/${type}?token=${token}`, "_blank");
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  if (!sol) return <p className="text-center text-slate-400 py-12">Solicitud no encontrada</p>;

  const canAddExpenses = (isOwner || isAdmin) && ["aprobado", "en_viaje", "en_liquidacion"].includes(sol.status);

  return (
    <div data-testid="viaticos-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="viaticos-detail-back">
        <CaretLeft size={16} /> Volver
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_COLORS[sol.status]}`}>{STATUS_LABELS[sol.status]}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{VISIT_LABELS[sol.visit_type] || sol.visit_type}</span>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-slate-600 mt-1">
              <span className="flex items-center gap-1"><User size={14} /> {sol.user_name}</span>
              <span className="flex items-center gap-1"><MapPin size={14} /> {sol.location}</span>
              <span className="flex items-center gap-1"><CalendarBlank size={14} /> {sol.date_start} → {sol.date_end} ({sol.num_days || 1} dia{(sol.num_days || 1) > 1 ? "s" : ""})</span>
              {sol.vehicle_plate && <span className="flex items-center gap-1"><Car size={14} /> {sol.vehicle_plate}</span>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-slate-800">${(sol.amount_requested || 0).toLocaleString()}</p>
            <p className="text-[11px] text-slate-400">Solicitado</p>
            {sol.approved_amount != null && (
              <>
                <p className="text-lg font-bold text-emerald-600 mt-1">${sol.approved_amount.toLocaleString()}</p>
                <p className="text-[11px] text-slate-400">Aprobado</p>
              </>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t">
          {isAdmin && sol.status === "solicitado" && (
            <button onClick={() => setShowApproveModal(true)} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 flex items-center gap-1" data-testid="viaticos-approve-btn">
              <Check size={14} /> Aprobar / Rechazar
            </button>
          )}
          {(isOwner || isAdmin) && sol.status === "aprobado" && (
            <button onClick={() => changeStatus("en_viaje")} className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 flex items-center gap-1" data-testid="viaticos-start-trip">
              <ArrowRight size={14} /> Iniciar Viaje
            </button>
          )}
          {(isOwner || isAdmin) && sol.status === "en_viaje" && (
            <button onClick={() => changeStatus("en_liquidacion")} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 flex items-center gap-1" data-testid="viaticos-start-liquidation">
              <Receipt size={14} /> Iniciar Liquidacion
            </button>
          )}
          {(isOwner || isAdmin) && sol.status === "en_liquidacion" && (
            <button onClick={() => changeStatus("liquidado")} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 flex items-center gap-1" data-testid="viaticos-complete-liquidation">
              <CheckCircle size={14} /> Completar Liquidacion
            </button>
          )}
          {isAdmin && sol.status === "liquidado" && (
            <button onClick={() => changeStatus("cerrado")} className="px-3 py-1.5 bg-slate-600 text-white rounded-lg text-xs font-medium hover:bg-slate-700 flex items-center gap-1" data-testid="viaticos-close">
              <Check size={14} /> Cerrar
            </button>
          )}

          {/* PDF buttons */}
          <div className="flex gap-2 ml-auto">
            <button onClick={() => exportPdf("solicitud")} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 flex items-center gap-1" data-testid="viaticos-pdf-solicitud">
              <FilePdf size={14} /> PDF Solicitud
            </button>
            <button onClick={() => exportPdf("liquidacion")} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 flex items-center gap-1" data-testid="viaticos-pdf-liquidacion">
              <FilePdf size={14} /> PDF Liquidacion
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit mb-4">
        {[
          { id: "info", label: "Detalle" },
          { id: "expenses", label: `Gastos (${sol.gastos?.length || 0})` },
          { id: "summary", label: "Resumen" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`viaticos-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* INFO TAB */}
      {tab === "info" && (
        <div className="space-y-4">
          {sol.description && (
            <div className="bg-white rounded-xl border p-4">
              <h4 className="text-xs font-medium text-slate-500 mb-1">Descripcion</h4>
              <p className="text-sm text-slate-700">{sol.description}</p>
            </div>
          )}

          {sol.clients?.length > 0 && (
            <div className="bg-white rounded-xl border p-4">
              <h4 className="text-xs font-medium text-slate-500 mb-2">Clientes a Visitar</h4>
              <div className="flex flex-wrap gap-2">
                {sol.clients.map((c, i) => <span key={typeof c === 'object' ? c.name || i : `${c}-${i}`} className="text-xs px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">{typeof c === 'object' ? c.name : c}</span>)}
              </div>
            </div>
          )}

          {sol.budget_items?.length > 0 && (
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-slate-500">Presupuesto por Categoria</h4>
                {isAdmin && !editingBudget && (
                  <button onClick={startEditBudget} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="viaticos-edit-budget-btn">
                    <PencilSimple size={12} /> Editar montos
                  </button>
                )}
              </div>
              {editingBudget ? (
                <div className="space-y-2">
                  {editBudgetItems.map((b, i) => (
                    <div key={b.category || i} className="flex items-center gap-2 text-sm">
                      <span className="w-24 text-slate-600 capitalize">{CATEGORY_LABELS[b.category] || b.category}</span>
                      <span className="text-xs text-slate-400">$</span>
                      <input type="number" value={b.amount_per_day || ""} onChange={e => {
                        const val = parseFloat(e.target.value) || 0;
                        setEditBudgetItems(prev => prev.map((item, idx) => idx === i ? { ...item, amount_per_day: val, amount: Math.round(val * (item.days || 1) * 100) / 100 } : item));
                      }} className="w-24 px-2 py-1 border rounded text-sm" data-testid={`edit-budget-${b.category}`} />
                      <span className="text-xs text-slate-400">x {b.days || 1} dia{(b.days || 1) > 1 ? "s" : ""} =</span>
                      <span className="font-medium text-slate-800">${((b.amount_per_day || 0) * (b.days || 1)).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-sm font-bold text-slate-700">Total: ${editBudgetItems.reduce((s, b) => s + ((b.amount_per_day || 0) * (b.days || 1)), 0).toLocaleString()}</span>
                    <div className="flex gap-2">
                      <button onClick={saveBudget} className="px-3 py-1 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 flex items-center gap-1" data-testid="save-budget-btn">
                        <FloppyDisk size={12} /> Guardar
                      </button>
                      <button onClick={() => setEditingBudget(false)} className="px-3 py-1 bg-slate-100 text-slate-600 rounded text-xs">Cancelar</button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {sol.budget_items.map((b, i) => (
                    <div key={b.category || i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 capitalize">{CATEGORY_LABELS[b.category] || b.category}</span>
                      <span className="text-slate-500 text-xs">${(b.amount_per_day || 0).toLocaleString()}/dia x {b.days || 1} dia{(b.days || 1) > 1 ? "s" : ""}</span>
                      <span className="font-medium text-slate-800">${(b.amount || 0).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-sm font-bold pt-1.5 border-t mt-1.5">
                    <span className="text-slate-700">Total</span>
                    <span className="text-slate-900">${sol.budget_items.reduce((s, b) => s + (b.amount || 0), 0).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {sol.vehicle_plate && (
            <div className="bg-white rounded-xl border p-4">
              <h4 className="text-xs font-medium text-slate-500 mb-2">Vehiculo</h4>
              <p className="text-sm font-medium text-slate-700">{sol.vehicle_name || sol.vehicle_plate}</p>
              {sol.odometer_start && <p className="text-xs text-slate-500 mt-1">Odometro salida: {sol.odometer_start} km</p>}
            </div>
          )}

          {sol.inventory_items?.length > 0 && (
            <div className="bg-white rounded-xl border p-4">
              <h4 className="text-xs font-medium text-slate-500 mb-2">Inventario / Material</h4>
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-1.5 text-xs text-slate-500">Descripcion</th><th className="text-left py-1.5 text-xs text-slate-500">No. Serie</th><th className="text-left py-1.5 text-xs text-slate-500">Condiciones</th></tr></thead>
                <tbody>
                  {sol.inventory_items.map((it, i) => (
                    <tr key={it.serial_number || `inv-${i}`} className="border-b last:border-0"><td className="py-1.5">{it.description}</td><td className="py-1.5 font-mono text-xs">{it.serial_number}</td><td className="py-1.5">{it.conditions}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-white rounded-xl border p-4">
            <h4 className="text-xs font-medium text-slate-500 mb-2">Seguridad</h4>
            <div className="flex items-center gap-2 mb-2">
              {sol.safety_social_security ? <CheckCircle size={16} className="text-emerald-500" weight="fill" /> : <X size={16} className="text-slate-300" />}
              <span className="text-sm text-slate-700">Hoja de Seguridad Social</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(SAFETY_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  {sol.safety_equipment?.[key] ? <CheckCircle size={14} className="text-emerald-500" weight="fill" /> : <X size={14} className="text-slate-300" />}
                  <span className="text-xs text-slate-600">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Method */}
          {sol.payment_method && (
            <div className="bg-white rounded-xl border p-4">
              <h4 className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1"><CreditCard size={14} /> Metodo de Pago</h4>
              <p className="text-sm font-medium text-slate-800">{PAYMENT_LABELS[sol.payment_method] || sol.payment_method}</p>
              {sol.payment_method === "transferencia" && (
                <div className="mt-2 space-y-1">
                  {sol.payment_transfer_id && <p className="text-xs text-slate-600">ID Transferencia: <span className="font-medium">{sol.payment_transfer_id}</span></p>}
                  {sol.payment_transfer_extra && <p className="text-xs text-slate-600">Referencia: <span className="font-medium">{sol.payment_transfer_extra}</span></p>}
                </div>
              )}
              {/* Payment evidence files */}
              {sol.payment_files?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500">Evidencia:</span>
                  {sol.payment_files.map(f => (
                    <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200">
                      <FileIcon size={12} /> {f.filename?.length > 20 ? f.filename.slice(0, 20) + "..." : f.filename}
                    </a>
                  ))}
                </div>
              )}
              {(isOwner || isAdmin) && (
                <label className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded cursor-pointer hover:bg-blue-100 w-fit mt-2" data-testid="viaticos-upload-payment-evidence">
                  <UploadSimple size={12} /> Subir evidencia de pago
                  <input type="file" className="hidden" accept="image/*,.pdf" onChange={async (e) => {
                    if (!e.target.files[0]) return;
                    const fd = new FormData();
                    fd.append("file", e.target.files[0]);
                    try {
                      await axios.post(`${API}/viaticos/solicitudes/${solicitudId}/payment-evidence`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
                      toast.success("Evidencia subida");
                      load();
                    } catch { toast.error("Error al subir evidencia"); }
                  }} />
                </label>
              )}
            </div>
          )}

          {sol.admin_notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="text-xs font-medium text-amber-700 mb-1">Notas del Administrador</h4>
              <p className="text-sm text-amber-800">{sol.admin_notes}</p>
              {sol.approved_by && <p className="text-xs text-amber-500 mt-1">— {sol.approved_by}, {sol.approved_at?.slice(0, 10)}</p>}
            </div>
          )}
        </div>
      )}

      {/* EXPENSES TAB */}
      {tab === "expenses" && (
        <div className="space-y-4">
          {canAddExpenses && (
            <div>
              {!showExpenseForm ? (
                <button onClick={() => setShowExpenseForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="viaticos-add-expense-btn">
                  <Plus size={16} /> Agregar Gasto
                </button>
              ) : (
                <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="viaticos-expense-form">
                  <h4 className="font-semibold text-slate-800">Nuevo Gasto</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Categoria</label>
                      <select value={expenseForm.category} onChange={e => setExpenseForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="expense-category">
                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Fecha *</label>
                      <input type="date" value={expenseForm.date} onChange={e => setExpenseForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="expense-date" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Monto *</label>
                      <div className="relative mt-0.5">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input type="number" value={expenseForm.amount} onChange={e => setExpenseForm(p => ({ ...p, amount: e.target.value }))} className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm" data-testid="expense-amount" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Motivo / Descripcion</label>
                    <input value={expenseForm.description} onChange={e => setExpenseForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Detalle del gasto" data-testid="expense-description" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Tipo de Comprobante</label>
                      <select value={expenseForm.receipt_type} onChange={e => setExpenseForm(p => ({ ...p, receipt_type: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="expense-receipt-type">
                        <option value="nota">Nota</option>
                        <option value="remision">Nota de Remision</option>
                        <option value="factura">Factura</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">No. Comprobante</label>
                      <input value={expenseForm.receipt_number} onChange={e => setExpenseForm(p => ({ ...p, receipt_number: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Folio o referencia" data-testid="expense-receipt-number" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Cargo a</label>
                      <div className="flex gap-2 mt-0.5">
                        <button onClick={() => setExpenseForm(p => ({ ...p, charge_to: "sti" }))}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${expenseForm.charge_to === "sti" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}
                          data-testid="expense-charge-sti">STI</button>
                        <button onClick={() => setExpenseForm(p => ({ ...p, charge_to: "cliente" }))}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${expenseForm.charge_to === "cliente" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}
                          data-testid="expense-charge-client">Cliente</button>
                      </div>
                    </div>
                    {expenseForm.charge_to === "cliente" && (
                      <div>
                        <label className="text-xs font-medium text-slate-500">Nombre del Cliente</label>
                        <input value={expenseForm.charge_client_name} onChange={e => setExpenseForm(p => ({ ...p, charge_client_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Nombre del cliente" data-testid="expense-client-name" />
                      </div>
                    )}
                  </div>
                  {/* Payment method per expense */}
                  <div>
                    <label className="text-xs font-medium text-slate-500">Metodo de Pago</label>
                    <select value={expenseForm.payment_method} onChange={e => setExpenseForm(p => ({ ...p, payment_method: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="expense-payment-method">
                      {Object.entries(PAYMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  {expenseForm.payment_method === "transferencia" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-500">ID Transferencia</label>
                        <input value={expenseForm.payment_transfer_id} onChange={e => setExpenseForm(p => ({ ...p, payment_transfer_id: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Referencia bancaria" data-testid="expense-transfer-id" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Referencia Extra</label>
                        <input value={expenseForm.payment_transfer_extra} onChange={e => setExpenseForm(p => ({ ...p, payment_transfer_extra: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Banco, cuenta" data-testid="expense-transfer-extra" />
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <button onClick={addExpense} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700" data-testid="expense-save">Guardar Gasto</button>
                    <button onClick={() => setShowExpenseForm(false)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200">Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expenses list */}
          {(!sol.gastos || sol.gastos.length === 0) ? (
            <div className="text-center py-8 bg-white rounded-xl border">
              <Receipt size={40} className="mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">No hay gastos registrados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sol.gastos.map(g => (
                <div key={g.id} className="bg-white rounded-xl border p-4" data-testid={`expense-card-${g.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{CATEGORY_ICONS[g.category] || "📦"}</span>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{CATEGORY_LABELS[g.category] || g.category}</p>
                        <p className="text-xs text-slate-500">{g.description}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span>{g.date}</span>
                          {g.receipt_type && <span className="capitalize">{g.receipt_type}: {g.receipt_number}</span>}
                          <span className={`font-medium ${g.charge_to === "cliente" ? "text-amber-600" : "text-blue-600"}`}>
                            Cargo: {g.charge_to === "cliente" ? `Cliente (${g.charge_client_name})` : "STI"}
                          </span>
                          {g.payment_method && (
                            <span className="text-purple-600 font-medium flex items-center gap-0.5">
                              <CreditCard size={11} /> {PAYMENT_LABELS[g.payment_method] || g.payment_method}
                              {g.payment_method === "transferencia" && g.payment_transfer_id ? ` (${g.payment_transfer_id})` : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex items-start gap-2">
                      <p className="text-lg font-bold text-slate-800">${(g.amount || 0).toLocaleString()}</p>
                      {isAdmin && canAddExpenses && (
                        <button onClick={() => deleteExpense(g.id)} className="text-red-400 hover:text-red-600 mt-1" data-testid={`delete-expense-${g.id}`}><Trash size={14} /></button>
                      )}
                    </div>
                  </div>
                  {/* Files */}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {g.files?.map(f => (
                      <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200">
                        <FileIcon size={12} /> {f.filename?.length > 20 ? f.filename.slice(0, 20) + "..." : f.filename}
                      </a>
                    ))}
                    {canAddExpenses && (
                      <label className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded cursor-pointer hover:bg-blue-100">
                        <UploadSimple size={12} /> Subir archivo
                        <input type="file" className="hidden" accept="image/*,.pdf" onChange={e => { if (e.target.files[0]) uploadFile(g.id, e.target.files[0]); }} />
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SUMMARY TAB */}
      {tab === "summary" && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border p-4 text-center">
              <p className="text-2xl font-bold text-slate-800">${(summary.amount_requested || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">Solicitado</p>
            </div>
            <div className="bg-white rounded-xl border p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">${(summary.approved_amount || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">Aprobado</p>
            </div>
            <div className="bg-white rounded-xl border p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">${(summary.total_spent || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">Gastado</p>
            </div>
            <div className="bg-white rounded-xl border p-4 text-center">
              <p className={`text-2xl font-bold ${(summary.difference || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                ${Math.abs(summary.difference || 0).toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1">{(summary.difference || 0) >= 0 ? "Sobrante" : "Excedente"}</p>
            </div>
          </div>

          {Object.keys(summary.by_category || {}).length > 0 && (
            <div className="bg-white rounded-xl border p-4">
              <h4 className="text-xs font-medium text-slate-500 mb-3">Gastos por Categoria</h4>
              <div className="space-y-2">
                {Object.entries(summary.by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                  <div key={cat} className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 flex items-center gap-2">
                      <span>{CATEGORY_ICONS[cat] || "📦"}</span> {CATEGORY_LABELS[cat] || cat}
                    </span>
                    <span className="font-medium text-sm text-slate-800">${amt.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(summary.by_charge?.sti > 0 || summary.by_charge?.cliente > 0) && (
            <div className="bg-white rounded-xl border p-4">
              <h4 className="text-xs font-medium text-slate-500 mb-3">Cargo a</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">${(summary.by_charge?.sti || 0).toLocaleString()}</p>
                  <p className="text-xs text-blue-500">STI</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-700">${(summary.by_charge?.cliente || 0).toLocaleString()}</p>
                  <p className="text-xs text-amber-500">Cliente</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* APPROVE MODAL */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowApproveModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="viaticos-approve-modal">
            <h3 className="font-bold text-lg text-slate-900">Aprobar / Rechazar Solicitud</h3>
            <div>
              <label className="text-xs font-medium text-slate-500">Monto a Aprobar</label>
              <div className="relative mt-0.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" value={approveData.approved_amount} onChange={e => setApproveData(p => ({ ...p, approved_amount: e.target.value }))}
                  className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm" data-testid="approve-amount" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Notas</label>
              <textarea value={approveData.admin_notes} onChange={e => setApproveData(p => ({ ...p, admin_notes: e.target.value }))}
                rows={3} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="approve-notes" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleApprove} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700" data-testid="approve-confirm">Aprobar</button>
              <button onClick={handleReject} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700" data-testid="approve-reject">Rechazar</button>
              <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
